import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './GoldCardSection.css';

const CARD_TYPES = [
  {
    id: 'individual',
    label: 'Individual',
    sublabel: 'Trump Gold Card',
    available: true,
    description: 'For individual applicants seeking lawful permanent residence.',
    price: '$5,000,000',
    features: ['Lawful Permanent Residence', 'Work Authorization', 'Travel Document', 'Path to Citizenship'],
  },
  {
    id: 'platinum',
    label: 'Individual',
    sublabel: 'Trump Platinum Card — Coming Soon',
    available: false,
    description: 'Enhanced benefits package. Registration opening soon.',
    price: 'TBD',
    features: ['All Gold Card Benefits', 'Priority Processing', 'Dedicated Case Officer'],
  },
  {
    id: 'corporate',
    label: 'Corporate',
    sublabel: 'Trump Corporate Gold Card',
    available: true,
    description: 'For corporate entities sponsoring qualifying employees.',
    price: '$5,000,000+',
    features: ['Multiple Beneficiaries', 'Business Investment Track', 'Corporate Case Management'],
  },
];

const INFO_BLOCKS = [
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
      </svg>
    ),
    title: 'Eligibility Requirements',
    body: 'Applicants must demonstrate a qualifying investment, clean immigration history, and meet background check standards established by DHS.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
      </svg>
    ),
    title: 'Investment Amount',
    body: 'A non-refundable investment of $5,000,000 USD is required per principal applicant. Payment must be made via approved channels.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    title: 'Processing Timeline',
    body: 'After Form I-140G submission, USCIS will begin background vetting. Estimated processing time is 60–90 days for initial determination.',
  },
  {
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    title: 'Family Members',
    body: 'Eligible derivative family members — spouses and unmarried children under 21 — may be included in the petition at no additional investment requirement.',
  },
];

export default function GoldCardSection() {
  const [selectedCard, setSelectedCard] = useState('individual');

  return (
    <>
      {/* Gold Card Hero Section */}
      <section className="gc-hero-section">
        <div className="container gc-hero-inner">
          <div className="gc-hero-text">
            <h2 className="gc-hero-title">The Trump Gold Card<br />is Here.</h2>
            <p className="gc-hero-subtitle">Unlock life in America.</p>
          </div>
          <div className="gc-card-visual">
            <img
              src="/images/gold-card.png"
              alt="Trump Gold Card"
              className="gc-card-img"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback rendered card */}
            <div className="gc-card-fallback" style={{ display: 'none' }}>
              <div className="gc-card-country">UNITED STATES OF AMERICA</div>
              <div className="gc-card-badge">US</div>
              <div className="gc-card-name">Trump<br />Gold Card</div>
              <div className="gc-card-stripe" />
            </div>
          </div>
        </div>
      </section>

      {/* Card Chooser */}
      <section className="gc-chooser-section">
        <div className="container">
          <h3 className="gc-chooser-title">Choose Your Card</h3>
          <div className="gc-card-options">
            {CARD_TYPES.map((card) => (
              <button
                key={card.id}
                className={`gc-card-option ${selectedCard === card.id ? 'active' : ''} ${!card.available ? 'disabled' : ''}`}
                onClick={() => card.available && setSelectedCard(card.id)}
                aria-pressed={selectedCard === card.id}
              >
                <span className="gc-option-label">{card.label}</span>
                <span className="gc-option-sub">{card.sublabel}</span>
                {!card.available && <span className="gc-option-badge">Coming Soon</span>}
              </button>
            ))}
          </div>

          {/* Selected card details */}
          {CARD_TYPES.filter(c => c.id === selectedCard).map(card => (
            <div key={card.id} className="gc-card-detail">
              <div className="gc-detail-left">
                <h4>{card.label} — {card.sublabel}</h4>
                <p>{card.description}</p>
                <div className="gc-price">Investment Required: <strong>{card.price}</strong></div>
                <Link to="/register" className="btn btn-primary btn-lg gc-apply-btn">
                  Apply for the {card.label} Gold Card
                </Link>
              </div>
              <div className="gc-detail-right">
                <h5>What's Included</h5>
                <ul className="gc-feature-list">
                  {card.features.map(f => (
                    <li key={f}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info Blocks */}
      <section className="gc-info-section">
        <div className="container">
          <h2 className="section-title">Program Details</h2>
          <p className="section-subtitle">
            The Trump Gold Card (Form I-140G) provides a new pathway to lawful permanent residence
            for qualified applicants worldwide.
          </p>
          <div className="gc-info-grid">
            {INFO_BLOCKS.map((block) => (
              <div key={block.title} className="gc-info-card">
                <div className="gc-info-icon">{block.icon}</div>
                <h4 className="gc-info-title">{block.title}</h4>
                <p className="gc-info-body">{block.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── VIDEO SECTION ── */}
      <section className="gc-video-section">
        <div className="container gc-video-inner">
          <div className="gc-video-text">
            <div className="gc-video-eyebrow">From the President</div>
            <h2 className="gc-video-title">President Trump Announces the Gold Card Program</h2>
            <p className="gc-video-body">
              President Donald J. Trump unveiled the Trump Gold Card initiative, describing it as
              a direct path to lawful permanent residence for qualified, vetted individuals who
              wish to invest in and contribute to the United States of America.
            </p>
            <ul className="gc-video-points">
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                A $5,000,000 non-refundable investment per principal applicant
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Includes eligible spouses and children under 21
              </li>
              <li>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="16">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
                Full DHS background vetting and processing via Form I-140G
              </li>
            </ul>
          </div>
          <div className="gc-video-frame">
            <div className="gc-video-wrapper">
              <iframe
                src="https://www.youtube.com/embed/i0sL9pgKz9Y"
                title="President Trump Announces the Gold Card Program"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <p className="gc-video-caption">
              White House Press Briefing — February 26, 2025
            </p>
          </div>
        </div>
      </section>

      {/* ── LEADERSHIP SECTION ── */}
      <section className="gc-leadership-section">
        <div className="container">
          <div className="gc-leadership-eyebrow">USCIS Leadership</div>
          <h2 className="gc-leadership-title">Agency Leadership</h2>
          <div className="gc-leadership-card">
            <div className="gc-leader-photo-wrapper">
              <img
                src="https://www.uscis.gov/sites/default/files/images/article-about-uscis/Alfonso-Royals_Angelica_8x10_v2.jpg"
                alt="Angelica Alfonso-Royals, USCIS Deputy Director"
                className="gc-leader-photo"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="gc-leader-photo-fallback" style={{ display: 'none' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <div className="gc-leader-photo-badge">
                <img
                  src="/images/logo.png"
                  alt="USCIS"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                <span>USCIS</span>
              </div>
            </div>
            <div className="gc-leader-info">
              <div className="gc-leader-meta">
                <span className="gc-leader-agency">U.S. Citizenship and Immigration Services</span>
                <span className="gc-leader-dept">Department of Homeland Security</span>
              </div>
              <h3 className="gc-leader-name">Angelica Alfonso-Royals</h3>
              <div className="gc-leader-title-badge">Deputy Director — USCIS</div>
              <p className="gc-leader-bio">
                Angelica Alfonso-Royals serves as the Deputy Director of U.S. Citizenship and
                Immigration Services (USCIS). A career member of the Senior Executive Service,
                she has served in various senior leadership roles at the Department of Homeland
                Security, including Chief of Staff to the Deputy Secretary of Homeland Security,
                Senior Advisor, and Chief Creative Officer.
              </p>
              <p className="gc-leader-bio">
                Alfonso-Royals previously served as acting director of USCIS and has worked
                under six USCIS directors, providing counsel on national and international
                immigration matters. She began her career as a press secretary on Capitol Hill
                and holds a Bachelor of Science in Political Science and Journalism from the
                University of Miami.
              </p>
              <div className="gc-leader-tags">
                <span>Senior Executive Service</span>
                <span>Dept. of Homeland Security</span>
                <span>Gold Card Program</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="gc-cta-banner">
        <div className="container gc-cta-inner">
          <div>
            <h3>Ready to Apply?</h3>
            <p>Create your account and begin your I-140G Gold Card petition today.</p>
          </div>
          <div className="gc-cta-actions">
            <Link to="/register" className="btn btn-gold btn-lg">Start Application</Link>
            <Link to="/login" className="btn btn-outline-white btn-lg">Sign In</Link>
          </div>
        </div>
      </section>
    </>
  );
}